import { type TaxFiling } from '@fruitful/types';

export interface RetirementCalculatorInputs {
    householdInputs: HouseholdInputs;
    memberInputs: MemberInput[];
}

export interface HouseholdInputs {
    annualSpendingInRetirement: number;
    targetSuccessLikelihood: number;
    taxFilingStatus: TaxFiling;
    residentialLocation: string;
    currentMarginalTaxRate?: number;
    retirementContributionWaterfall?: RetirementContributionWaterfall;
}

export interface MemberInput {
    id: string;
    currentAge: number;
    retirementAge: number;
    socialSecurityBenefitAge: number;
    lifeExpectancy: number;
    currentGrossAnnualIncome: number;
    currentRetirementSavings: RetirementAssets;
    hasEmployerRetirementPlan: boolean;
    employerMatchMaximumPercentOfIncome?: number;
    employerMatchPercentage?: number;
    isSelfEmployed: boolean;
    annualSocialSecurityBenefit?: number;
    includeRetirementHealthcareCosts: boolean;
    retirementHealthcareCost: number;
    longTermCareCost: number;
}

export type RetirementAssets = RetirementAccount[];

export interface RetirementAccount {
    id: string;
    owner: string;
    type: RetirementAccountType;
    balance: number;
    equitiesPercentage: number;
    costBasis: number; // Original investment amount (for tax calculations)
}

export enum RetirementAccountTaxTreatment {
    ROTH = 'ROTH',
    NON_ROTH = 'NON_ROTH',
    TAXABLE = 'TAXABLE',
}

export enum RetirementAccountType {
    EMPLOYER_401K = 'EMPLOYER_401K',
    ROTH_401K = 'ROTH_401K',
    SEP_IRA = 'SEP_IRA',
    SOLO_401K = 'SOLO_401K',
    ROTH_IRA = 'ROTH_IRA',
    TRAD_IRA = 'TRAD_IRA',
    BACKDOOR_ROTH_IRA = 'BACKDOOR_ROTH_IRA',
    TAXABLE_BROKERAGE = 'TAXABLE_BROKERAGE',
}

export const retirementAccountTaxTreatment: Record<
    RetirementAccountType,
    RetirementAccountTaxTreatment
> = {
    EMPLOYER_401K: RetirementAccountTaxTreatment.NON_ROTH,
    ROTH_401K: RetirementAccountTaxTreatment.ROTH,
    SEP_IRA: RetirementAccountTaxTreatment.NON_ROTH,
    SOLO_401K: RetirementAccountTaxTreatment.NON_ROTH,
    ROTH_IRA: RetirementAccountTaxTreatment.ROTH,
    TRAD_IRA: RetirementAccountTaxTreatment.NON_ROTH,
    BACKDOOR_ROTH_IRA: RetirementAccountTaxTreatment.ROTH,
    TAXABLE_BROKERAGE: RetirementAccountTaxTreatment.TAXABLE,
};

export type RetirementContributionWaterfall = Array<{
    memberId: string;
    contributions: Partial<Record<RetirementAccountType, number>>; // number is the percent (represented in decimal, 0-1) of the contribution that should be directed to the account type. mostly used to indicate contribution splits between roth and non-roth retirement accounts. e.g. a 401k contribution for a member could be represented as { memberId: 123, contributions: { EMPLOYER_401K: 0.5, ROTH_401k: 0.5} }
}>;

export interface RetirementCalculatorOutputs {
    requiredAnnualRetirementSavings: number;
    requiredAnnualContributions: RetirementContributions; // breakdown of requiredAnnualRetirementSavings, by account.
    annualSavingsOutcomes: Record<number, SimulatedRetirementSavingsOutcome>;
}

export interface SimulatedRetirementSavingsOutcome {
    successLikelihood: number;
    averageCashFlowByYear: AnnualCashFlow[];
}

export interface AnnualCashFlow {
    year: number;
    primaryMemberAge: number;
    partnerMemberAge?: number;
    primaryMemberYearType: YearType;
    partnerMemberYearType?: YearType;
    primaryMemberSocialSecurityBenefit: number;
    partnerMemberSocialSecurityBenefit?: number;
    totalSocialSecurityTax: number;
    retirementExpenses: number;
    retirementDrawdown: number;
    retirementHealthcareCosts: number;
    longTermCareCosts: number;
    totalRetirementExpenses: number;
    startingAssets: RetirementAssets;
    contributions: RetirementContributions;
    growth: RetirementGrowth;
    distributions: RetirementDistribution[];
    endingAssets: RetirementAssets;
    effectiveTaxRate: number;
    insufficientFunds: boolean;
}

export enum YearType {
    ACCUMULATION = 'ACCUMULATION',
    DECUMULATION = 'DECUMULATION',
    END_OF_LIFE = 'END_OF_LIFE',
}

export type RetirementContributions = Array<{
    account: RetirementAccount;
    employeeContribution: number;
    employerContribution?: number;
    totalContribution: number;
}>;

export enum DistributionType {
    RMD = 'RMD',
    DRAWDOWN = 'DRAWDOWN',
}

export type RetirementDistribution = GrossRetirementDistribution & {
    taxAmount: number;
    netDistributionAmount: number;
};

export interface GrossRetirementDistribution {
    account: RetirementAccount;
    grossDistributionAmount: number;
    taxableAmount: number;
    type: DistributionType;
}

export type RetirementGrowth = Array<{
    account: RetirementAccount;
    growth: number;
}>;

export interface RetirementDistributionResult {
    totalNetInflows: number;
    distributions: RetirementDistribution[];
    totalSocialSecurityTax: number;
    effectiveTaxRate: number;
    targetMet: boolean;
}
